import { Button } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import { Accepted, AcceptenceStatus, Declined } from 'app/models/Placement/PlacementMods';
import { useSelector } from 'react-redux';
import {
  selectIsEditExistingMod,
  selectIsEditMod,
  selectIsEmiLmoDisabled,
  selectIsAcceptanceStatusEditable,
} from 'store/redux-store/placement-mods/selectors';

const acceptanceStatusStyle = makeStyles<{ currentSelected: string }>()((theme, { currentSelected }) => ({
  labelStyle: {
    textAlign: 'left',
    fontFamily: 'Roboto',
    fontSize: '12px',
    fontWeight: 'bold',
    color: theme.palette.system.black,
  },
  accepptedBtn: {
    color: currentSelected === Accepted ? theme.palette.components.button.text.approved.color : theme.palette.framework.system.doveGray,
    fontSize: '12px',
    fontWeight: 'bold',
    '&:hover': {
      color: theme.palette.components.button.text.approved.color,
      backgroundColor: 'transparent',
    },
    '& .MuiButton-startIcon': {
      marginRight: '3px',
    },
  },
  declinedBtn: {
    color: currentSelected === Declined ? theme.palette.framework.system.red : theme.palette.framework.system.doveGray,
    fontSize: '12px',
    fontWeight: 'bold',
    '&:hover': {
      color: theme.palette.framework.system.red ,
      backgroundColor: 'transparent',
    },
    '& .MuiButton-startIcon': {
      marginRight: '3px',
    },
  },
  alignBtn: {
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
  },
  statusMargin: {
    marginTop: '24px',
  },
}));

export const AcceptanceStatusSection = dialogProps => {
  const { t } = useTranslation();
  const { currentModDetails, selectedButton, setSelectedButtons, undoButtonChanges, setUndoButtonChanges, 
    modsDialogDetails } = dialogProps;
  const [currentSelected, setCurrentSelected] = useState(getAcceptanceStatus());
  const [existingStatus, setExistingStatus] = useState(getAcceptanceStatus());
  const { classes } = acceptanceStatusStyle({ currentSelected });
  const isModEdit = useSelector(selectIsEditMod);
  const isEditExistingMod = useSelector(selectIsEditExistingMod);
  const isEmiLmoDisabled = useSelector(selectIsEmiLmoDisabled);
  const isAcceptanceStatusEditable = useSelector(selectIsAcceptanceStatusEditable);

  // Helper function to determine if the modification is a bonus type
  const isBonusModification = () => {
    const selectedMod = modsDialogDetails?.selectedMod?.replaceAll(/['/.\s]/g, '');
    return selectedMod === 'Bonus';
  };

  function getAcceptanceStatus() {
    switch (currentModDetails.acceptanceStatusId) {
      case 1:
        return Accepted;
      case 2:
        return Declined;
    }
  }

  const acceptanceStatusButtons = [
    {
      id: Accepted,
      component: (
        <Button
          variant="text"
          className={classes.accepptedBtn}
          disabled={selectedButton?.isDelete || selectedButton?.isVoid || isEmiLmoDisabled}
          startIcon={<DoneIcon />}
          onClick={() => {
            if (isBonusModification() && !isAcceptanceStatusEditable) {
              return; // Ignore click for bonus modifications when not editable
            }
            setCurrentSelected(Accepted);
          }}
        >
          {t('placement.placementMods.accepted')}
        </Button>
      ),
    },
    {
      id: Declined,
      component: (
        <Button
          variant="text"
          className={classes.declinedBtn}
          disabled={selectedButton?.isDelete || selectedButton?.isVoid || isEmiLmoDisabled}
          startIcon={<CloseIcon />}
          onClick={() => {
            if (isBonusModification() && !isAcceptanceStatusEditable) {
              return; // Ignore click for bonus modifications when not editable
            }
            setCurrentSelected(Declined);
          }}
        >
          {t('placement.placementMods.declined')}
        </Button>
      ),
    },
  ];

  const setInitialValues = () => {
    setCurrentSelected(getAcceptanceStatus());
    setExistingStatus(getAcceptanceStatus());
    setAcceptanceStatusOptions(getAcceptanceStatusOptions());
    setUndoButtonChanges(false);
  };

  const [acceptanceStatusOptions, setAcceptanceStatusOptions] = useState(getAcceptanceStatusOptions());

  useEffect(() => {
    if (currentModDetails && undoButtonChanges) {
      setInitialValues();
    } else if (currentModDetails) {
      setInitialValues();
    }
  }, [currentModDetails, undoButtonChanges]);

  useEffect(() => {
    setAcceptanceStatusOptions(getAcceptanceStatusOptions());
  }, [
    currentSelected,
    selectedButton?.isDelete,
    selectedButton?.isVoid,
    currentModDetails,
    isEditExistingMod,
    isModEdit,
    isAcceptanceStatusEditable,
  ]);

  useEffect(() => {
    if (currentSelected) {
      setSelectedButtons({ ...selectedButton, acceptedStatus: AcceptenceStatus[currentSelected] });
    }
  }, [currentSelected]);

  function getAcceptanceStatusOptions() {
    const acceptanceOptions = [];

    currentModDetails?.acceptanceStatusOptions?.forEach(el => {
      const arr = acceptanceStatusButtons.filter(val => val.id === el.value)[0];
      if (arr) acceptanceOptions.push(arr);
    });
    if (!isModEdit && !isEditExistingMod) {
      return [];
    } else {
      return acceptanceOptions;
    }
  }

  return (
    <div
      style={selectedButton?.isDelete || selectedButton?.isVoid ? { opacity: 0.5 } : null}
      className={classes.statusMargin}
    >
      <label className={classes.labelStyle}>{t('placement.placementMods.acceptanceStatus') + ':'}</label>
      {acceptanceStatusButtons
        .filter(btn => btn.id === existingStatus)
        .map((btn, index) => {
          return <React.Fragment key={index}>{btn.component}</React.Fragment>;
        })}
      <div className={classes.alignBtn}>
        {acceptanceStatusOptions.map((btn, index) => {
          return <React.Fragment key={index}>{btn.component}</React.Fragment>;
        })}
      </div>
    </div>
  );
};
